import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FALLBACK_LOGO,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";
import logo from "../assets/image/eblackdog_logo.png"

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
    totalGuards: 0,
    activeRoutes: 0,
    hasIsActive: false,
  });

  const [sentinelData, setSentinelData] = useState({
    totalGuards: 0,
    totalRoutes: 0,
    loading: true,
    error: false,
  });

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData((prev) => ({ ...prev, ...local }));
    fetchDashboard(local, token);
  }, [navigate]);

  const fetchDashboard = async (local, token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to fetch dashboard data");

      const result = await res.json();
      const apiData = result?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      setDashboardData((prev) => ({
        ...prev,
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
        totalGuards: apiData.totalGuards ?? 0,
        activeRoutes: apiData.totalRoutes ?? 0,
        hasIsActive: false,
      }));

      setSentinelData({
        totalGuards: apiData.sentinelGuards ?? 0,
        totalRoutes: apiData.sentinelRoutes ?? 0,
        loading: false,
        error: false,
      });
    } catch (error) {
      console.error("Dashboard fetch error:", error);

      setDashboardData((prev) => ({
        ...prev,
        ...local,
        totalGuards: 0,
        activeRoutes: 0,
        hasIsActive: false,
      }));

      setSentinelData({
        totalGuards: 0,
        totalRoutes: 0,
        loading: false,
        error: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const isActive = Number(dashboardData.admin_active) === 1;
  const logoSrc = dashboardData.company_logo?.trim() || logo;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading dashboard...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="dashboard"
      activeMenu="dashboard"
    >
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Company License Management
        </h2>

        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          Licensed company information & operational details
        </p>
      </div>

      <CompanyCard
        logoSrc={logo}
        dashboardData={dashboardData}
        isActive={isActive}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <ContactCard dashboardData={dashboardData} />
        <StatsCard dashboardData={dashboardData} sentinelData={sentinelData} />
      </div>
    </Layout>
  );
}

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function CompanyCard({ logoSrc, dashboardData, isActive }) {
  const skin = useSkin();

  return (
    <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 flex items-center gap-6 flex-wrap">
      <div className="w-24 h-24 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
        <img
          src={logoSrc}
          alt="Company Logo"
          className="w-full h-full object-contain"
        />
      </div>

      <div className="flex-1 min-w-[220px]">
        <h3
          className="m-0 mb-1 text-[26px] font-normal text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          {dashboardData.company_name || "Company Name"}
        </h3>

        <p className="mb-3 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {dashboardData.licensedFor || "Licensed Service"}
        </p>

        <span
          className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold ${
            isActive
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-600"
          }`}
          style={{ fontFamily: FONT_UI }}
        >
          {isActive ? "● Active" : "● Inactive"}
        </span>
      </div>

      <div
        className="w-1.5 h-24 rounded-full shrink-0"
        style={{ background: skin.gradient }}
      />
    </div>
  );
}

function ContactCard({ dashboardData }) {
  const skin = useSkin();

  const items = [
    { label: "Address", icon: "📍", value: dashboardData.company_address },
    { label: "Phone", icon: "📞", value: dashboardData.company_phone },
    { label: "Email", icon: "✉", value: dashboardData.admin_email },
  ];

  return (
    <Card>
      <SectionTitle title="Contact Information" skin={skin} />

      <div>
        {items.map(({ label, icon, value }, index) => (
          <div
            key={label}
            className={`py-3.5 flex gap-3 items-start ${
              index < items.length - 1 ? "border-b border-slate-100" : ""
            }`}
          >
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-sm shrink-0"
              style={{ background: skin.light }}
            >
              {icon}
            </div>

            <div>
              <p
                className="m-0 mb-0.5 text-[11px] text-slate-400 uppercase tracking-wider font-semibold"
                style={{ fontFamily: FONT_UI }}
              >
                {label}
              </p>

              <p className="m-0 text-sm text-slate-800" style={{ fontFamily: FONT_UI }}>
                {value || "—"}
              </p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function StatsCard({ dashboardData, sentinelData }) {
  const skin = useSkin();

  return (
    <Card>
      <SectionTitle title="Operational Statistics" skin={skin} />

      <p
        className="m-0 mb-3 text-[11px] font-bold uppercase tracking-widest text-slate-400"
        style={{ fontFamily: FONT_UI }}
      >
        Main Flow
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        <StatTile
          label="Total Guards"
          value={dashboardData.totalGuards}
          className="text-white"
          style={{ background: skin.gradient }}
        />

        <StatTile
          label="Total Routes"
          value={dashboardData.activeRoutes}
          className="bg-slate-50 border border-slate-200"
          valueStyle={{ color: skin.primary }}
          labelClassName="text-slate-500"
          circleStyle={{ background: skin.light }}
        />
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-slate-100" />

        <div
          className="flex items-center gap-1.5 px-3 py-1 rounded-full border"
          style={{
            background: skin.light,
            borderColor: `${skin.accent}30`,
          }}
        >
          <ShieldIcon color={skin.accent} />

          <span
            className="text-[11px] font-bold uppercase tracking-wider"
            style={{
              color: skin.primary,
              fontFamily: FONT_UI,
            }}
          >
            Sentinel Flow
          </span>
        </div>

        <div className="flex-1 h-px bg-slate-100" />
      </div>

      {sentinelData.loading ? (
        <div className="flex items-center justify-center py-5 gap-3">
          <div
            className="w-5 h-5 rounded-full border-[3px] animate-spin"
            style={{
              borderColor: skin.light,
              borderTopColor: skin.accent,
            }}
          />

          <span className="text-sm text-slate-400" style={{ fontFamily: FONT_UI }}>
            Loading sentinel data...
          </span>
        </div>
      ) : sentinelData.error ? (
        <div className="px-4 py-3.5 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2">
          <ErrorIcon />

          <span className="text-sm text-red-600" style={{ fontFamily: FONT_UI }}>
            Unable to load sentinel data
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SentinelTile
            label="Sentinel Guards"
            value={sentinelData.totalGuards}
            skin={skin}
          />

          <SentinelTile
            label="Sentinel Routes"
            value={sentinelData.totalRoutes}
            skin={skin}
          />
        </div>
      )}

      <div className="mt-5 flex gap-3 flex-wrap">
        {[
          { label: "View Users", to: "/user_list" },
          { label: "Route List", to: "/route_list" },
          { label: "Analytics", to: "/analytics" },
        ].map(({ label, to }) => (
          <Link
            key={label}
            to={to}
            className="px-4 py-2 rounded-lg text-sm font-semibold no-underline"
            style={{
              background: skin.light,
              color: skin.primary,
              border: `1px solid ${skin.accent}30`,
              fontFamily: FONT_UI,
            }}
          >
            {label} →
          </Link>
        ))}
      </div>
    </Card>
  );
}

function Card({ children }) {
  return (
    <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100">
      {children}
    </div>
  );
}

function SectionTitle({ title, skin }) {
  return (
    <h4
      className="m-0 mb-5 text-[15px] font-semibold text-slate-800 tracking-tight flex items-center gap-2"
      style={{ fontFamily: FONT_UI }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full inline-block"
        style={{ background: skin.accent }}
      />
      {title}
    </h4>
  );
}

function StatTile({
  label,
  value,
  className = "",
  style = {},
  valueStyle = {},
  labelClassName = "text-white/80",
  circleStyle = { background: "rgba(255,255,255,0.1)" },
}) {
  return (
    <div
      className={`rounded-2xl px-4 py-5 relative overflow-hidden ${className}`}
      style={style}
    >
      <div
        className="absolute -right-3 -top-3 w-16 h-16 rounded-full"
        style={circleStyle}
      />

      <p
        className={`m-0 mb-1 text-[10px] font-bold uppercase tracking-wider ${labelClassName}`}
        style={{ fontFamily: FONT_UI }}
      >
        {label}
      </p>

      <p
        className="m-0 text-[32px] font-extrabold leading-none"
        style={{
          fontFamily: FONT_UI,
          ...valueStyle,
        }}
      >
        {value}
      </p>
    </div>
  );
}

function SentinelTile({ label, value, skin }) {
  return (
    <div
      className="rounded-2xl px-4 py-5 relative overflow-hidden border"
      style={{
        background: `${skin.primary}10`,
        borderColor: `${skin.accent}25`,
      }}
    >
      <div
        className="absolute -right-3 -top-3 w-16 h-16 rounded-full"
        style={{ background: `${skin.accent}15` }}
      />

      <div className="flex items-center gap-1.5 mb-1.5">
        <ShieldIcon color={skin.accent} />

        <p
          className="m-0 text-[10px] font-bold uppercase tracking-wider"
          style={{
            color: skin.accent,
            fontFamily: FONT_UI,
          }}
        >
          {label}
        </p>
      </div>

      <p
        className="m-0 text-[32px] font-extrabold leading-none"
        style={{
          color: skin.primary,
          fontFamily: FONT_UI,
        }}
      >
        {value}
      </p>
    </div>
  );
}

function ShieldIcon({ color = "#2563eb" }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#ef4444"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}